"use client";

import type { ImpactPartnerProfessor } from "@mw/shared/payload-types";

import ChevronRight16 from "@ui/icons/16x16/chevron-right-16.svg";
import { ProfessorImpactDialogButton } from "@/app/_components/HubspotForms/ProfessorImpactForm";
import { ListTable, ListTableRow } from "@ui/components/core/ListTable";
import { useTranslations } from "next-intl";
import { useState } from "react";
import ImpactPartnerProfessorPopup from "./ImpactPartnerProfessorPopup";

interface ImpactPartnerProfessorsListViewProps {
  allProfessors: ImpactPartnerProfessor[];
  displayProfessors: ImpactPartnerProfessor[];
}

export default function ImpactPartnerProfessorsListView({
  allProfessors,
  displayProfessors,
}: ImpactPartnerProfessorsListViewProps) {
  const t = useTranslations("ClientKeys.ImpactPartnerProfessorsList");
  const tProfessorImpact = useTranslations("ClientKeys.professor_impact_form.dialog");
  const [selectedProfessor, setSelectedProfessor] = useState<ImpactPartnerProfessor | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const listProfessors = displayProfessors.length > 0 ? displayProfessors : allProfessors;

  const handleRowClick = (professor: ImpactPartnerProfessor) => {
    setSelectedProfessor(professor);
    setIsPopupOpen(true);
  };

  return (
    <div>
      <ListTable
        header={
          <div className='tw-grid tw-grid-cols-2 tw-items-center tw-gap-4 tw-py-4'>
            <p className='tw-type-xs tw-text-black tw-opacity-60'>{t("professor_label")}</p>
            <p className='tw-type-xs tw-hidden tw-text-black tw-opacity-60 md:tw-block'>{t("university_label")}</p>
          </div>
        }
      >
        {listProfessors.map(professor => (
          <ListTableRow key={professor.id}>
            <button
              type='button'
              className='tw-grid tw-w-full tw-grid-cols-2 tw-items-center tw-gap-4 tw-py-2.5 tw-text-left tw-transition-colors hover:tw-bg-white'
              onClick={() => handleRowClick(professor)}
              aria-label={professor.name}
            >
              <div className='tw-min-w-0'>
                <span className='tw-block tw-truncate tw-type-base tw-mb-1 tw-py-1'>{professor.name}</span>
                {professor.credentials && (
                  <span className='tw-mt-1 tw-hidden tw-truncate tw-type-xs tw-text-black tw-opacity-60 tw-py-1 md:tw-block'>
                    {professor.credentials}
                  </span>
                )}
                <span className='tw-mt-1 tw-block tw-truncate tw-type-xs tw-text-black tw-opacity-60 tw-py-1 md:tw-hidden'>
                  {professor.university}
                </span>
              </div>
              <div className='tw-flex tw-min-w-0 tw-items-center tw-justify-end tw-gap-4 md:tw-justify-between'>
                <span className='tw-hidden tw-truncate tw-type-base tw-py-1 md:tw-block'>{professor.university}</span>
                <ChevronRight16 className='tw-size-4 tw-shrink-0 tw-text-black tw-opacity-60' aria-hidden />
              </div>
            </button>
          </ListTableRow>
        ))}
      </ListTable>
      <div className='tw-mt-14 tw-flex tw-flex-col tw-items-center tw-text-center'>
        <h4 className='tw-type-base tw-py-1 tw-mb-1.5'>{tProfessorImpact("title")}</h4>
        <p className='tw-type-xs tw-mb-4 tw-max-w-md tw-text-gray-500 tw-py-1'>{tProfessorImpact("description")}</p>
        <ProfessorImpactDialogButton buttonVariant='elevated' buttonSize='md' />
      </div>
      <ImpactPartnerProfessorPopup
        professors={allProfessors}
        selectedProfessor={selectedProfessor}
        setSelectedProfessor={setSelectedProfessor}
        open={isPopupOpen}
        onOpenChange={setIsPopupOpen}
      />
    </div>
  );
}
